import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Folder, 
  FileText, 
  Image, 
  File,
  ArrowLeft,
  Download,
  ExternalLink,
  RefreshCw,
  ChevronRight,
  Calendar,
  HardDrive
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

const formatSize = (bytes) => {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const FileIcon = ({ type, extension }) => {
  if (type === 'image') {
    return <Image className="w-5 h-5 text-purple-500" />;
  }
  if (type === 'pdf') {
    return <FileText className="w-5 h-5 text-red-500" />;
  }
  if (type === 'document') {
    return <FileText className="w-5 h-5 text-blue-500" />;
  }
  if (type === 'spreadsheet') {
    return <FileText className="w-5 h-5 text-green-500" />;
  }
  return <File className="w-5 h-5 text-gray-500" />;
};

const AdminUploadFolder = () => {
  const { '*': folderPath } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/upload-settings/folder/${folderPath || ''}`);
      setData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load folder contents');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [folderPath]);

  // Build breadcrumb from path
  const buildBreadcrumb = () => {
    if (!folderPath) return [];
    const parts = folderPath.split('/').filter(Boolean);
    return parts.map((part, idx) => ({
      name: part,
      path: parts.slice(0, idx + 1).join('/')
    }));
  };

  const breadcrumb = buildBreadcrumb();

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Link 
                to="/admin/upload-settings" 
                className="text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Upload Settings
              </Link>
              {breadcrumb.length > 0 && (
                <>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                  {breadcrumb.map((crumb, idx) => (
                    <React.Fragment key={idx}>
                      {idx < breadcrumb.length - 1 ? (
                        <>
                          <Link 
                            to={`/admin/upload-settings/folder/${crumb.path}`}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            {crumb.name}
                          </Link>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </>
                      ) : (
                        <span className="text-gray-800 font-medium">{crumb.name}</span>
                      )}
                    </React.Fragment>
                  ))}
                </>
              )}
            </div>
            <h1 className="text-2xl font-semibold text-gray-800">
              {folderPath?.split('/').pop() || 'Root'}
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              {data?.full_path}
            </p>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium transition"
            style={{ backgroundColor: BRAND_BLUE }}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Folder Info */}
        {data && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <span className="text-gray-500">Files:</span>
                <span className="ml-2 font-medium text-gray-800">{data.total_files}</span>
              </div>
              <div>
                <span className="text-gray-500">Subfolders:</span>
                <span className="ml-2 font-medium text-gray-800">{data.total_subfolders}</span>
              </div>
            </div>
          </div>
        )}

        {/* Subfolders */}
        {data?.subfolders?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-800 mb-3">Subfolders</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
              {data.subfolders.map((subfolder, idx) => (
                <Link
                  key={idx}
                  to={`/admin/upload-settings/folder/${subfolder.relative_path}`}
                  className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition"
                >
                  <Folder className="w-8 h-8 text-amber-500" />
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800 truncate">{subfolder.name}</p>
                    <p className="text-xs text-gray-500">{subfolder.relative_path}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Files */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-800">
              Files ({data?.files?.length || 0})
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
              >
                <FileText className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
              >
                <HardDrive className="w-4 h-4" />
              </button>
            </div>
          </div>

          {data?.files?.length === 0 ? (
            <div className="p-12 text-center">
              <Folder className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No files in this folder</p>
            </div>
          ) : viewMode === 'list' ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">File Name</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Type</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Size</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Modified</th>
                    <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.files.map((file, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <FileIcon type={file.file_type} extension={file.extension} />
                          <div className="min-w-0">
                            <p className="font-medium text-gray-800 truncate max-w-md" title={file.name}>
                              {file.name}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium uppercase">
                          {file.extension?.replace('.', '') || 'unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {file.size_formatted}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatDate(file.modified)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                            title="View"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                          <a
                            href={file.url}
                            download={file.name}
                            className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded transition"
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {data.files.map((file, idx) => (
                <div key={idx} className="group relative">
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition"
                  >
                    {file.file_type === 'image' ? (
                      <div className="aspect-square mb-3 bg-gray-100 rounded overflow-hidden">
                        <img 
                          src={file.url} 
                          alt={file.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="aspect-square mb-3 bg-gray-100 rounded flex items-center justify-center">
                        <FileIcon type={file.file_type} extension={file.extension} />
                      </div>
                    )}
                    <p className="text-sm font-medium text-gray-800 truncate" title={file.name}>
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{file.size_formatted}</p>
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminUploadFolder;
